import { createApp } from "vue";
import { createPinia } from "pinia";
import "./assets/css/main.css";

import App from "./app.vue";
import router from "./router";
import { useThemeStore } from "./stores/theme";
import { useLanguageStore } from "./stores/language";
import { useUserStore } from "./stores/user";
import { useServerStore } from "./stores/server";
import { useUiThemeStore } from "./themes/useUiTheme";
import { initGoogleTagManager, trackGtmRouteChanges } from "./utils/gtm";

import * as Sentry from "@sentry/vue";

async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    // Tracing
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SR, // Capture 100% of the traces
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  });

  app.use(pinia);
  app.use(router);

  // Sync color scheme and language from localStorage before mount (the inline
  // script in index.html already prevents the initial color flash).
  useThemeStore().hydrateFromStorage();
  useLanguageStore().hydrateFromStorage();

  // Profile can load in parallel; UI theme must be known before first paint.
  useUserStore().fetchProfile();

  try {
    const serverStore = useServerStore();
    await serverStore.fetchServerInfo();

    if (!serverStore.unavailable) {
      initGoogleTagManager(serverStore.googleTagManagerId);
      trackGtmRouteChanges(router);
    }

    await useUiThemeStore().activate(
      serverStore.unavailable ? "default" : serverStore.theme,
    );
  } catch {
    try {
      await useUiThemeStore().activate("default");
    } catch {
      // app.vue shows a loading fallback if no layout is available
    }
  }

  app.mount("#app");
}

bootstrap();
